import type { SEOHandle } from "@nasa-gcn/remix-seo";
import type { MetaFunction } from "@remix-run/node";
import { Link } from "@remix-run/react";

import blogContent from "~/content/blog";
import { useLanguage } from "~/context/LanguageContext";
import { sortedPosts } from "~/data/blog";
import { useLocalizedPath } from "~/helpers/navigationHelper";
import blogDict from "~/i18n/blog";
import { useT } from "~/i18n/useT";
import { buildMeta, langFromParams } from "~/seo/meta";

import "./blog.scss";

export const meta: MetaFunction = ({ params }) => {
  const lang = langFromParams(params);
  const t = blogDict[lang];

  return buildMeta({
    lang,
    path: "/blog",
    title: t.metaTitle,
    description: t.metaDescription,
    englishOnly: true,
  });
};

// Only the English blog is indexed, so only English URLs go in the sitemap.
export const handle: SEOHandle = {
  getSitemapEntries: async () => [
    { route: "/blog", priority: 0.6 as const, changefreq: "weekly" as const },
    ...sortedPosts().map((post) => ({
      route: `/blog/${post.slug}`,
      priority: 0.6 as const,
      changefreq: "monthly" as const,
    })),
  ],
};

export default function BlogIndexPage() {
  const t = useT(blogDict);
  const { lang } = useLanguage();
  const localize = useLocalizedPath();
  const posts = sortedPosts();

  return (
    <main id="main-content">
      <section className="ss-container ss-container--narrow ss-blog">
        <p className="ss-eyebrow ss-eyebrow--blue">{t.eyebrow}</p>
        <h1 className="ss-blog__title">{t.title}</h1>
        <p className="ss-blog__lead">{t.lead}</p>

        {lang !== "en" ? (
          <p className="ss-blog__notice">{t.englishOnlyNotice}</p>
        ) : null}

        <div className="ss-blog__list">
          {posts.map((post) => {
            const content = blogContent[post.slug];

            return (
              <article key={post.slug} className="ss-post-card">
                <Link
                  to={localize(`/blog/${post.slug}`)}
                  className="ss-post-card__link"
                >
                  <div className="ss-post-card__media">
                    <img
                      src={post.image}
                      alt=""
                      width={280}
                      height={180}
                      loading="lazy"
                      decoding="async"
                    />
                  </div>

                  <div className="ss-post-card__body">
                    <p className="ss-post-card__meta">
                      <span className="ss-tag ss-tag--blue">{post.tag}</span>
                      <time dateTime={post.date}>
                        {new Intl.DateTimeFormat("en", {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        }).format(new Date(post.date))}
                      </time>
                      <span>{t.readTime(post.readingMinutes)}</span>
                    </p>

                    <h2 className="ss-post-card__title">{content.title}</h2>
                    <p className="ss-post-card__excerpt">{content.excerpt}</p>
                    <span className="ss-link-mono">
                      {t.readMore}
                      <span className="ss-flip" aria-hidden="true">
                        →
                      </span>
                    </span>
                  </div>
                </Link>
              </article>
            );
          })}
        </div>
      </section>
    </main>
  );
}
